/**
 * SKIN-600 — the API's refusal of a dermatologist's web session outside its correction work.
 *
 * SKIN-599 bounds that session in the API: anything outside the visits flagged to it answers
 * `403 { errorCode: "WEB_SESSION_OUT_OF_SCOPE" }` (sherwood-api
 * `docs/features/quality-check/correction-permission.md`). It is not an expired session — only a
 * 401 ends one (`sessionExpiryInterceptors`) — so the session is kept, and whoever listens (the
 * dashboard shell) tells the user the screen is outside their correction work.
 *
 * Installed on the same clients as the session-expiry interceptors, for the reason that module
 * states: on the `<500`-validating client a 403 fulfils, on the global one it rejects.
 */
export const WEB_SESSION_OUT_OF_SCOPE = 'WEB_SESSION_OUT_OF_SCOPE';

type ResponseInterceptable = {
    interceptors: { response: { use: (onFulfilled: (r: any) => any, onRejected: (e: any) => any) => unknown } };
};

/** Whether an AxiosError, or a response, is this refusal. */
export function isWebSessionOutOfScope(errorOrResponse: unknown): boolean {
    const candidate = errorOrResponse as { response?: { status?: number; data?: any }; status?: number; data?: any } | undefined;
    const response = candidate?.response ?? candidate;
    return response?.status === 403 && response?.data?.errorCode === WEB_SESSION_OUT_OF_SCOPE;
}

const listeners = new Set<() => void>();

/** Subscribe to the refusal; returns the unsubscribe. */
export function onWebSessionOutOfScope(listener: () => void): () => void {
    listeners.add(listener);
    return () => { listeners.delete(listener); };
}

function notifyIfOutOfScope(errorOrResponse: unknown): void {
    if (isWebSessionOutOfScope(errorOrResponse)) listeners.forEach((listener) => listener());
}

/** The response and the error pass through untouched: callers keep their own control flow. */
export function installWebSessionScopeInterceptors(clients: ResponseInterceptable[]): void {
    clients.forEach((client) => {
        client.interceptors.response.use(
            (response: any) => {
                notifyIfOutOfScope(response);
                return response;
            },
            (error: any) => {
                notifyIfOutOfScope(error);
                return Promise.reject(error);
            },
        );
    });
}
